/*
  Окно «Должник» (20.10.2026): внести человека со связью со счётом или поправить саму связь.

  При внесении спрашиваются сведения о человеке (те же поля, что в заявлении) и связь:
  роль, вид ответственности, доля и срок. При правке связи - только связь: сведения о
  человеке (паспорт, ИНН) правит отдельное окно «Поправить сведения» на его карточке, и
  правка видна везде, где человек встречается.

  Доля вводится в процентах («50»), на сервер идёт долей от единицы («0.5000»): человек
  думает процентами, а сервер хранит долю. Доля - только при долевой ответственности;
  при солидарной поле не показывается, а на сервер уходит `null`.
*/

import { useState } from 'react'

import { api } from '../../api/client.js'
import { LIABILITY_TITLES, PERSON_ROLE_TITLES } from '../../api/format.js'
import { Field } from '../Common.jsx'
import Dropdown from '../Select.jsx'
import FormDialog from './FormDialog.jsx'

const PERSON_FIELDS = [
  'last_name',
  'first_name',
  'middle_name',
  'birth_date',
  'birth_place',
  'inn',
  'snils',
  'passport',
  'phone',
  'email',
]

function percentOf(share) {
  if (share === null || share === undefined) return ''
  return String(Number((Number(share) * 100).toFixed(2)))
}

function startForm(link) {
  const form = {
    role: link?.role ?? 'owner',
    liability: link?.liability ?? 'solidary',
    share: percentOf(link?.share),
    valid_from: link?.valid_from ?? '',
    valid_to: link?.valid_to ?? '',
  }
  for (const name of PERSON_FIELDS) form[name] = ''
  return form
}

function shareOf(form) {
  if (form.liability !== 'shared') return null
  return (Number(form.share.replace(',', '.')) / 100).toFixed(4)
}

function validShare(form) {
  if (form.liability !== 'shared') return true
  const value = Number(form.share.replace(',', '.'))
  return form.share.trim() !== '' && value > 0 && value <= 100
}

export default function ParticipantDialog({ accountId, link, onSaved, onClose }) {
  const [form, setForm] = useState(() => startForm(link))
  const set = (name) => (event) => setForm({ ...form, [name]: event.target.value })
  const editing = Boolean(link)

  function linkBody() {
    return {
      role: form.role,
      liability: form.liability,
      share: shareOf(form),
      valid_from: form.valid_from === '' ? null : form.valid_from,
      valid_to: form.valid_to === '' ? null : form.valid_to,
    }
  }

  function submit() {
    if (editing) return api.editParticipant(accountId, link.link_id, linkBody())
    const person = {}
    for (const name of PERSON_FIELDS) person[name] = form[name].trim() === '' ? null : form[name].trim()
    return api.addParticipant(accountId, { ...person, ...linkBody() })
  }

  const text = (name, label, width = 'm') => (
    <Field label={label} width={width}>
      <input className="field__input" value={form[name]} onChange={set(name)} />
    </Field>
  )

  const filled = editing || (form.last_name.trim() !== '' && form.first_name.trim() !== '')

  return (
    <FormDialog
      title={editing ? 'Поправить связь должника со счётом' : 'Внести должника'}
      note={editing ? link.person.full_name : undefined}
      submitLabel={editing ? 'Сохранить' : 'Внести должника'}
      canSubmit={filled && validShare(form)}
      wide={!editing}
      onSubmit={submit}
      onSaved={onSaved}
      onClose={onClose}
    >
      {editing ? null : (
        <>
          <div className="row">
            {text('last_name', 'Фамилия')}
            {text('first_name', 'Имя')}
            {text('middle_name', 'Отчество')}
          </div>
          <div className="row">
            <Field label="Дата рождения" width="s">
              <input
                className="field__input"
                type="date"
                value={form.birth_date}
                onChange={set('birth_date')}
              />
            </Field>
            {text('birth_place', 'Место рождения', 'l')}
          </div>
          <div className="row">
            {text('inn', 'ИНН', 's')}
            {text('snils', 'СНИЛС', 's')}
            {text('passport', 'Паспорт', 's')}
            {text('phone', 'Телефон', 's')}
            {text('email', 'Почта')}
          </div>
        </>
      )}
      <div className="row">
        <div className="field field--s">
          <span className="field__label" id="participant-role-label">
            Роль
          </span>
          <Dropdown
            value={form.role}
            onChange={(role) => setForm({ ...form, role })}
            options={PERSON_ROLE_TITLES}
            labelledBy="participant-role-label"
          />
        </div>
        <div className="field field--s">
          <span className="field__label" id="participant-liability-label">
            Ответственность
          </span>
          <Dropdown
            value={form.liability}
            onChange={(liability) => setForm({ ...form, liability })}
            options={LIABILITY_TITLES}
            labelledBy="participant-liability-label"
          />
        </div>
        {form.liability === 'shared' ? (
          <Field label="Доля, %" width="xs">
            <input
              className="field__input"
              inputMode="decimal"
              value={form.share}
              onChange={set('share')}
              placeholder="50"
            />
          </Field>
        ) : null}
      </div>
      <div className="row">
        <Field label="Отвечает с" width="s">
          <input
            className="field__input"
            type="date"
            value={form.valid_from}
            onChange={set('valid_from')}
          />
        </Field>
        <Field label="Отвечает по" width="s">
          <input
            className="field__input"
            type="date"
            value={form.valid_to}
            onChange={set('valid_to')}
          />
        </Field>
      </div>
    </FormDialog>
  )
}
